import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DestinationType } from '../../../redux/models/statistics/destination-type';
import { KpiSummaryData } from '../../../redux/models/statistics/kpi-summary-data';
import { Statistics } from '../../../redux/models/statistics/statistics';
import { StatisticsFilter } from '../../../redux/models/statistics/statistics-filter';
import { StatisticsType, statisticsTypes } from '../../../redux/models/statistics/statistics-type';
import { TopDestinationFilter } from '../../../redux/models/statistics/top-destination-filter';
import { StatisticDataAdapter } from './utils/statistic-data-adapter';
import {
  isLoading,
  isOfficesRequestSending,
  selectAllOfficeIds,
  selectCurrentKpis
} from './redux/selectors/dashboard-selector';
import { Kpis } from './redux/states/dashboard-state';
import { KpiSummaryTransformerService } from './redux/services/kpi-summary-transformer.service';
import { DashboardService } from './../../../redux/services/dashboard/dashboard.service';
import { getCustomerConfigState, selectAVCTACurrency } from '../../../redux/reducers';
import { RequestBookingOffices } from './redux/actions/actions-dashboard';
import { customerConfig } from '@uxdf/direct-shared-lib';

@Component({
  standalone: false,
  selector: 'app-dashboard-avc',
  templateUrl: './dashboard-avc.component.html',
  styleUrls: ['./dashboard-avc.component.scss']
})
export class DashboardAVCComponent implements OnInit, OnDestroy {
  public DestinationType = DestinationType;
  public statisticsTypes = statisticsTypes;
  currentFilter: StatisticsFilter;
  statisticsType = StatisticsType.REVENUES;
  statistics: Statistics[];
  kpiSummaryData: KpiSummaryData[];
  hasStatisticsData: boolean;
  hasOffices: boolean;
  viewAVCDashboards: boolean;

  topDestinationsTab = 1;
  topDestinationsFilter: TopDestinationFilter;

  isRequestLoading$: Observable<boolean>;
  isOfficesLoading$: Observable<boolean>;
  isSnow = false;
  private customerConfigSubscription: Subscription;

  readonly labels = {
    [StatisticsType.GROSS_SALES]: $localize`:@@amadeus-direct.avc-dashboard.statistics-type.gross-sales:Gross sales`,
    [StatisticsType.NUMBER_OF_BOOKINGS]: $localize`:@@amadeus-direct.avc-dashboard.statistics-type.number-of-bookings:Bookings`,
    [StatisticsType.REVENUES]: $localize`:@@amadeus-direct.avc-dashboard.statistics-type.revenues:Revenue`,
    [StatisticsType.NET_REVENUES]: $localize`:@@amadeus-direct.avc-dashboard.statistics-type.net-revenues:Net revenue`
  };
  readonly title = $localize`:@@amadeus-direct.avc-dashboard.title:Value Hotels dashboard`;

  private readonly componentDestroyed = new Subject();
  private currency: string;

  constructor(
    private readonly statisticsService: KpiSummaryTransformerService,
    private readonly store: Store<any>,
    private readonly statisticDataAdapter: StatisticDataAdapter,
    private readonly dashboardService: DashboardService
  ) {}

  ngOnInit(): void {
    this.isRequestLoading$ = this.store.pipe(select(isLoading));
    this.isOfficesLoading$ = this.store.pipe(select(isOfficesRequestSending));

    this.store.dispatch(new RequestBookingOffices());

    this.store.select(getCustomerConfigState).subscribe(config => {
      this.viewAVCDashboards = config.customerConfig?.is_avc_dashboards;
    });

    this.store.pipe(select(selectAllOfficeIds)).subscribe(offices => {
      this.hasOffices = offices?.length > 0;
    });

    this.store.select(selectAVCTACurrency).subscribe(currency => {
      this.currency = currency;
    });

    this.customerConfigSubscription = this.store.pipe(
        select(customerConfig)).subscribe(response => {
            this.isSnow = response.customerConfig.is_snow;
        });
  }

  onFilterChange(filter: StatisticsFilter): void {
    const newTopDestinationsFilter = {
      startDate: filter.startDate,
      endDate: filter.endDate,
      officeIds: filter.officeIds
    };
    if (JSON.stringify(newTopDestinationsFilter) !== JSON.stringify(this.topDestinationsFilter)) {
      this.topDestinationsFilter = newTopDestinationsFilter;
    }
    this.currentFilter = filter;
    this.setTopDestinationsTab();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next(undefined);
    this.componentDestroyed.complete();
    if (this.customerConfigSubscription) {
      this.customerConfigSubscription.unsubscribe();
    }
  }

  loadData(): void {
    this.isRequestLoading$.subscribe(loading => {
      if (!loading) {
        this.store.pipe(select(selectCurrentKpis)).subscribe((kpis: Kpis) => {
          this.hasStatisticsData = kpis?.totalBookings > 0;
        });
        this.getStatistics();
        this.getKPIStatistics();
      }
    });
  }

  getStatistics(): void {
    this.statisticDataAdapter
      .adaptStatistic(this.statisticsType, this.currentFilter, this.currency)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(statistics => {
        if (JSON.stringify(statistics) !== JSON.stringify(this.statistics)) {
          this.statistics = statistics;
        }
      });
  }

  getKPIStatistics(): void {
    this.statisticsService
      .transformKpi(this.statisticsType, this.currentFilter, this.currency)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(kpiStatistics => {
        if (JSON.stringify(kpiStatistics) !== JSON.stringify(this.kpiSummaryData)) {
          this.kpiSummaryData = kpiStatistics;
        }
      });
  }

  areCountriesDisabled(): boolean {
    if (!this.currentFilter || !this.currentFilter?.destination) {
      return false;
    }
    return this.currentFilter.destination.type === DestinationType.CITY && this.currentFilter.destination.code !== '';
  }

  setTopDestinationsTab(): void {
    if (this.areCountriesDisabled()) {
      this.topDestinationsTab = 1;
    }
  }

  print(): void {
    window.print();
  }
}
